import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  getDocs as fetchDocs,
} from "firebase/firestore";
import { createPortal } from "react-dom";

interface Device {
  id: string;
  battery_capacity: number;
  battery_size: string;
  batch: string;
  color: string;
  eva: string;
  firmware: string;
  manufacturer: string;
  motor: string;
  motors: number;
  pcb: string;
  product: string;
}

interface AttributeOptions {
  battery_capacity: number[];
  battery_size: string[];
  batch: string[];
  color: string[];
  eva: string[];
  firmware: string[];
  manufacturer: string[];
  motor: string[];
  motors: number[];
  pcb: string[];
  product: string[];
}

const DeviceManager: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [devices, setDevices] = useState<Device[]>([]);
  const [searchId, setSearchId] = useState<string>("");
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [bulkAttributes, setBulkAttributes] = useState<Partial<Device>>({});
  const [bulkIds, setBulkIds] = useState<string[]>([]);
  const [bulkInputId, setBulkInputId] = useState<string>("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [attributeOptions, setAttributeOptions] = useState<AttributeOptions | null>(null);
  const [searchError, setSearchError] = useState(false);

  const fetchData = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "devices"));
      const deviceList: Device[] = querySnapshot.docs
        .filter((doc) => doc.id !== "attributes")
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Device[];
      setDevices(deviceList);
    } catch (err) {
      console.error("Error fetching devices:", err);
    }
  };

  const fetchAttributeOptions = async () => {
    try {
      const docRef = doc(db, "devices", "attributes");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setAttributeOptions(docSnap.data() as AttributeOptions);
      } else {
        console.warn("No attribute options found.");
      }
    } catch (err) {
      console.error("Error fetching attribute options:", err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchAttributeOptions();
  }, []);

  const handleSearchDevice = () => {
    const found = devices.find((d) => d.id.toLowerCase() === searchId.toLowerCase());
    if (!found) {
      setSearchError(true);
      setSelectedDevice(null);
    } else {
      setSelectedDevice(found);
      setSearchError(false);
    }
  };

  const handleAddBulkDevice = async () => {
    try {
      const existingIds = new Set(devices.map((d) => d.id));
      const filtered = bulkIds.filter((id) => !existingIds.has(id));

      if (filtered.length === 0) {
        alert("Device ID already exists.");
        return;
      }

      for (const id of filtered) {
        await addDoc(collection(db, "devices"), { id, ...bulkAttributes });
      }

      setBulkIds([]);
      setBulkAttributes({});
      fetchData();
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 3000);
    } catch (err) {
      console.error("Error during bulk add:", err);
    }
  };

  return (
    <>
      <li>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(true);
          }}
        >
          Device Manager
        </a>
      </li>

      {isOpen &&
        createPortal(
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="relative bg-white p-8 rounded-3xl shadow-2xl w-11/12 lg:w-3/4 max-h-[80vh] overflow-auto">
                 <h2 className="text-2xl font-bold mb-4">Manage Devices</h2>
              <button
                className="btn btn-sm btn-circle absolute right-4 top-4"
                onClick={() => setIsOpen(false)}
              >
                ✕
              </button>

              <div className="mb-4">
                <label htmlFor="searchInput" className="block mb-2 font-semibold">
                  Search device by ID:
                </label>
                <div className="flex gap-2">
                  <input
                    id="searchInput"
                    className="input input-bordered w-full"
                    placeholder="Enter ID"
                    value={searchId}
                    onChange={(e) => setSearchId(e.target.value)}
                  />
                  <button
                    className="btn btn-accent"
                    onClick={handleSearchDevice}
                  >
                    Search
                  </button>
                </div>
                {searchError && (
                  <div className="mt-2 text-red-600 text-sm">
                    ❌ No device found with ID "{searchId}"
                  </div>
                )}
              </div>

              {selectedDevice && (
                <div className="bg-gray-100 p-4 rounded-xl mb-6">
                  <h3 className="text-xl font-semibold mb-2">Device Details</h3>
                  <ul className="space-y-1">
                    {Object.entries(selectedDevice)
                      .filter(([key]) => key !== "id")
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([key, value]) => (
                        <li key={key}>
                          <strong>{key.at(0)!.toUpperCase() + key.slice(1)}:</strong> {value.toString().toUpperCase()}
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              <div className="bg-blue-50 p-6 rounded-xl shadow-inner mb-6">
                <h3 className="text-xl font-semibold mb-4">Add Devices</h3>
                <div className="flex items-center gap-4 mb-4">
                  <input
                    className="input input-bordered flex-1"
                    placeholder="Enter Device ID"
                    value={bulkInputId}
                    onChange={(e) => setBulkInputId(e.target.value)}
                  />
                  <button
                    className="btn btn-ghost btn-circle"
                    onClick={() => {
                      const id = bulkInputId.toLowerCase();
                      if (id && !bulkIds.includes(id)) {
                        setBulkIds((prev) => [...prev, id]);
                        setBulkInputId("");
                      }
                    }}
                  >
                    ➕
                  </button>
                </div>
                <ul className="mb-4 space-y-1">
                  {bulkIds.map((id) => (
                    <li key={id} className="flex justify-between items-center bg-white px-3 py-2 rounded shadow">
                      <span>{id}</span>
                      <button
                        className="text-red-600 font-bold text-lg hover:text-red-800"
                        onClick={() => setBulkIds((prev) => prev.filter((x) => x !== id))}
                      >
                        −
                      </button>
                    </li>
                  ))}
                </ul>
                {attributeOptions &&
                  (Object.keys(attributeOptions) as (keyof AttributeOptions)[]).sort().map((field) => (
                    <div key={field} className="mb-3">
                      <label className="block font-medium mb-1">{field.at(0)!.toUpperCase() + field.slice(1)}</label>
                      <select
                        className="select select-bordered w-full"
                        value={bulkAttributes[field] ?? ""}
                        onChange={(e) => setBulkAttributes({
                          ...bulkAttributes,
                          [field]: e.target.value === "N/A" ? null : isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value),
                        })}
                      >
                        <option value="">-- Select {field} --</option>
                        <option value="N/A">N/A</option>
                        {attributeOptions[field]?.map((opt) => (
                          <option key={opt.toString()} value={opt.toString()}>{opt.toString().toUpperCase()}</option>
                        )).sort((a, b) => a.props.children.localeCompare(b.props.children))}
                      </select>
                    </div>
                  ))}
                <button
                  className="btn btn-primary w-full mt-2"
                  onClick={handleAddBulkDevice}
                >
                  Add
                </button>
                {showConfirmation && (
                  <div className="mt-4 p-3 bg-green-100 text-green-800 text-center rounded-xl animate-bounce shadow-md">
                    ✅ Device(s) successfully added!
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default DeviceManager;
